import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Card,
  Divider,
  Paper,
  Grid,
  Typography,
  TextField,
  Box,
  Stack,
  Collapse, // Import Collapse
  CircularProgress, // Import CircularProgress for loading states
  Tooltip, // Import Tooltip
} from "@mui/material";
import FilterListIcon from '@mui/icons-material/FilterList'; // Using a different filter icon
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import axios from "axios";
import HOST_URL from "../../../utils/Url"; // Assuming correct path
import ToastApp from "../../../ToastApp"; // Assuming correct path
import { toast } from "react-toastify";
import { useFormik } from "formik";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import PrintIcon from "@mui/icons-material/Print";
import { styled } from "@mui/material/styles";
import Dialog, { DialogProps } from "@mui/material/Dialog";
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { TransitionProps } from "@mui/material/transitions";
import Slide from "@mui/material/Slide";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
import { DeleteOutline, AddCircleOutline, Visibility, Comment as CommentIcon, PlaylistAddCheck } from "@mui/icons-material"; // Add relevant icons
import { useNavigate } from "react-router-dom";
import CustomizedProgressBars from "../../../components/Loader"; // Assuming correct path
import { getSecureDevId } from "../../../utils/storageHelpers";
// --- Styled Components (Keep as they are good) ---
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.primary.main, // Consistent header bg
    color: theme.palette.common.white,
    fontWeight: 600,
    padding: '12px 16px',
    whiteSpace: 'nowrap', // Prevent header text wrapping
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: '10px 16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    // Default: Allow wrapping for body cells unless overridden by sx prop
    // wordBreak: 'break-word', // Helps break long words if needed
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  cursor: 'default',
}));

// --- Transition for Dialogs (Keep as is) ---
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// --- Style for Text Clamping ---
const cellTextClampStyle = {
  display: '-webkit-box',
  WebkitLineClamp: 3, // Limit to 3 lines
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word', // Allow breaking words if needed
  maxHeight: '4.5em', // Approx 3 lines height (adjust based on line-height)
  lineHeight: '1.5em', // Ensure line height is consistent
};

// --- Row Component ---
function Row({ row, index }: { row: any; index: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  var someDate = new Date();
  var date = someDate.setDate(someDate.getDate());
  var defaultValues = new Date(date).toISOString().split("T")[0];

  const [maxWidth, setMaxWidth] = React.useState<DialogProps["maxWidth"]>("lg");
  const [openCommentModal, setOpenCommentModal] = useState(false);
  const [openPdfModal, setOpenPdfModal] = useState(false);
  const [pdfData, setPDFData] = useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [active, setActive] = useState(false);



  const [selectedRowInfo, setSelectedRowInfo] = useState<{
    pdfname: string;
    pfid: string;
    fileNo: string;
  } | null>(null);

  const initialRowData = {
    dPgid: -1, pgNF: 0, pgNU: 0, pdFid: 0, comments: "", chiPDFid: 0,
    createdate: defaultValues, user_Id: 0, userId: 0, phySNo: "",
    phyIndex: "", relOfficer: "", createdateNew: defaultValues,
    fileName: "", delete: false,
  };

  const [tableData, setTableData] = useState([initialRowData]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    idx: number, // Renamed from index to idx to avoid conflict
    field: keyof typeof initialRowData
  ) => {
    const { value } = e.target;
    setTableData(prevData =>
      prevData.map((item, i) =>
        i === idx ? { ...item, [field]: (field === 'pgNF' || field === 'pgNU') ? Math.max(0, parseInt(value, 10) || 0) : value } : item // Ensure numbers are non-negative
      )
    );
  };

  const handleAddCommentClick = (pdfname: string, pfid: string, fileNo: string) => {
    setSelectedRowInfo({ pdfname, pfid, fileNo });
    // TODO: Fetch existing comments for this pfid here if desired
    setTableData([{ ...initialRowData, pdFid: parseInt(pfid, 10) || 0 }]); // Reset/load comments, ensure pfid is number
    setOpenCommentModal(true);
  };

  const handleCloseCommentModal = () => {
    setOpenCommentModal(false);
    setSelectedRowInfo(null);
    setTableData([initialRowData]); // Reset on close
  };

  const handleViewPdfClick = (docMid: any, pdfName: string) => {
    setSelectedRowInfo({ pdfname: pdfName, pfid: docMid, fileNo: '' });
    setOpenPdfModal(true);
    getFileData(docMid);
  };

  const handleClosePdfModal = () => {
    setOpenPdfModal(false);
    setPDFData(null);
    setSelectedRowInfo(null);
  };

  const removeRow = (idx: number) => {
    setTableData(prevData => prevData.filter((_, i) => i !== idx));
  };



  const getDocMng = (pdfId: any) => {
    const collectData = {
      dPgid: -1,
      pdFid: pdfId,
      chiPDFid: -1,
      user_Id: -1,
      userId: -1,
    };
    axios
      .post(HOST_URL.HOST_URL2 + `DocFPages/GetDocFPages`, collectData)
      .then((res) => {
        const arr: any = [];
        console.log("result" + JSON.stringify(res.data.data));
        for (let index = 0; index < res.data.data.length; index++) {
          arr.push({

            pgNF: res.data.data[index]["pgNF"],
            pgNU: res.data.data[index]["pgNU"],
            phyIndex: res.data.data[index]["phyIndex"],
            comments: res.data.data[index]["comments"],
            phySNo: res.data.data[index]["phySNo"],
            relOfficer: res.data.data[index]["relOfficer"],
            pdFid: res.data.data[index]["pdFid"],
            fileName: res.data.data[index]["fileName"],
            pdfPath: res.data.data[index]["pdfPath"],
            isSelected: false,


            "dPgid": res.data.data[index]["dPgid"],
            "chiPDFid": res.data.data[index]["chiPDFid"],
            "user_Id": res.data.data[index]["user_Id"],
            "userId": res.data.data[index]["userId"],
            "createdateNew": defaultValues || "",
            "delete": true,
            "otherDocfile": res.data.data[index]["otherDocfile"] || "",
            "commentOthers": res.data.data[index]["commentOthers"] || "",

          });
        }
        setTableData(arr);
      });
  };

  const addMoreRow = () => {
    const newRow = {
      ...initialRowData,
      pdFid: selectedRowInfo?.pfid ? parseInt(selectedRowInfo.pfid, 10) : 0, // Ensure pdFid is number
      fileName: selectedRowInfo?.pdfname || "",
    };
    setTableData(prevData => [...prevData, newRow]);
  };

  const handleSubmitComments = async () => {
    if (!selectedRowInfo) return;
    setIsSubmittingComment(true);

    const userIdFromSomewhere = 0; // Replace with actual logged-in user ID logic

    const updatedTableData = tableData.map((rowData: any) => ({
      ...rowData,
      pdFid: parseInt(selectedRowInfo.pfid, 10) || 0, // Ensure pfid is correctly assigned and is number
      fileName: selectedRowInfo.pdfname,
      user_Id: userIdFromSomewhere, // Make sure user_Id is set correctly
      pgNF: Number(rowData.pgNF) || 0, // Ensure numbers are sent
      pgNU: Number(rowData.pgNU) || 0,
    }));

    const validData = updatedTableData.filter(d => d.pgNF !== 0 || d.pgNU !== 0 || d.comments?.trim() !== "");

    if (validData.length === 0) {
      toast.info(t("text.PleaseAddCommentOrPage")); // Updated message
      setIsSubmittingComment(false);
      return;
    }

    console.log("Submitting Comments:", validData);

    try {
      const response = await axios.post(
        `${HOST_URL.HOST_URL2}DocFPages/AddUpdateDocFPages`,
        { docFPages: validData }
      );
      if (response.data.isSuccess === true) {
        toast.success(response.data.mesg || t("text.CommentsSavedSuccess"));
        // handleCloseCommentModal();
        setActive(true)
      } else {
        toast.error(response.data.mesg || t("text.FailedSaveComments"));
      }
    } catch (error) {
      console.error("Error submitting comments:", error);
      toast.error(t("text.ErrorSavingComments"));
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleNavigateToSplit = (rowData: any) => {
    navigate("/DocManagement/SplitPage", { // Ensure this route is correct
      state: { ...rowData }, // Pass relevant data
    });
  };

  
const getFileData = async (docMid: any) => {
  setIsLoadingPdf(true);
  setPDFData(null);

  const collectData = {
    pdFid: parseInt(docMid, 10) || 0,
    user_Id: -1,
  };

  console.log("Fetching PDF Data for:", collectData);

  try {
    const { data } = await axios.post(`${HOST_URL.HOST_URL2}DocFiles/GetDocFiles`, collectData);

    if (data?.isSuccess && Array.isArray(data.data) && data.data.length > 0) {
      const pdfPath = data.data[0]?.pdfPath;

      if (pdfPath) {
        // Use the direct PDF path from the API response
        setPDFData(pdfPath);
      } else {
        toast.warn(t("text.PDFDataEmpty"));
      }
    } else {
      toast.error(data?.mesg || t("text.CouldNotRetrievePDF"));
    }
  } catch (error) {
    console.error("Error fetching PDF data:", error);
    toast.error(t("text.FailedLoadPDF"));
  } finally {
    setIsLoadingPdf(false);
  }
};
  // const getFileData = async (docMid: any) => {
  //   setIsLoadingPdf(true);
  //   setPDFData(null);

  //   const collectData = {
  //     pdFid: parseInt(docMid, 10) || 0,
  //     user_Id: -1,
  //   };

  //   console.log("Fetching PDF Data for:", collectData);

  //   try {
  //     const { data } = await axios.post(`${HOST_URL.HOST_URL2}DocFiles/GetDocFiles`, collectData);

  //     if (data?.isSuccess && Array.isArray(data.data) && data.data.length > 0) {
  //       const base64Pdf = data.data[0]?.pdfBase64;

  //       if (base64Pdf) {
  //         // Optimize the base64 handling
  //         const formattedPdf = base64Pdf.startsWith('data:application/pdf;base64,')
  //           ? base64Pdf
  //           : `data:application/pdf;base64,${base64Pdf}`;

  //         // Use Blob URL for better memory management
  //         const byteCharacters = atob(formattedPdf.split(',')[1]);
  //         const byteNumbers = new Array(byteCharacters.length);
  //         for (let i = 0; i < byteCharacters.length; i++) {
  //           byteNumbers[i] = byteCharacters.charCodeAt(i);
  //         }
  //         const byteArray = new Uint8Array(byteNumbers);
  //         const blob = new Blob([byteArray], { type: 'application/pdf' });
  //         const blobUrl = URL.createObjectURL(blob);

  //         setPDFData(blobUrl); // Store the Blob URL instead of base64
  //       } else {
  //         toast.warn(t("text.PDFDataEmpty"));
  //       }
  //     } else {
  //       toast.error(data?.mesg || t("text.CouldNotRetrievePDF"));
  //     }
  //   } catch (error) {
  //     console.error("Error fetching PDF data:", error);
  //     toast.error(t("text.FailedLoadPDF"));
  //   } finally {
  //     setIsLoadingPdf(false);
  //   }
  // };

  // Clean up Blob URL when component unmounts or PDF changes
  useEffect(() => {
    return () => {
      if (pdfData && pdfData.startsWith('blob:')) {
        URL.revokeObjectURL(pdfData);
      }
    };
  }, [pdfData]);


  // --- Render Row ---
  return (
    <React.Fragment>
      <StyledTableRow>
        <StyledTableCell align="center">{index + 1}</StyledTableCell>
        {/* Actions Cell */}
        <StyledTableCell sx={{ whiteSpace: 'nowrap' }}>
          <Tooltip title={t("text.Comment")} arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                handleAddCommentClick(row.pdfName, row.pdFid, row.fileNo);
                getDocMng(row.pdFid)
              }}
            >
              <CommentIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("text.Index")} arrow>
            <IconButton
              size="small"
              color="secondary"
              onClick={() => handleNavigateToSplit(row)}
              sx={{ ml: 0.5 }} // Add slight margin
            >
              <PlaylistAddCheck fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("text.ViewFullPdf")} arrow>
            <IconButton
              size="small"
              // color="info" // Optional color
              onClick={() => handleViewPdfClick(row.pdFid, row.pdfName)}
              sx={{ ml: 0.5 }}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
        </StyledTableCell>
        {/* Removed separate cells for Comment, Index, ViewPDF - combined into one Actions cell */}
        <StyledTableCell title={row.pdfName || ''}> {/* Hover title for PDF Name */}
          {/* Display PDF Name (visually truncated if needed, but hover shows full) */}
          <Typography variant="body2" sx={{ ...cellTextClampStyle, WebkitLineClamp: 1, maxHeight: '1.5em' }}> {/* Clamp to 1 line */}
            {row.pdfName || '-'}
          </Typography>
        </StyledTableCell>
        <StyledTableCell title={row.fileDef || '-'}>
          <Typography variant="body2" sx={cellTextClampStyle}>
            {row.fileDef || '-'}
          </Typography>
        </StyledTableCell>
        <StyledTableCell title={row.subSubject || '-'}>
          <Typography variant="body2" sx={cellTextClampStyle}>
            {row.subSubject || '-'}
          </Typography>
        </StyledTableCell>
        <StyledTableCell title={row.keywords || '-'}>
          <Typography variant="body2" sx={cellTextClampStyle}>
            {row.keywords || '-'}
          </Typography>
        </StyledTableCell>
        <StyledTableCell align="center" sx={{ whiteSpace: 'nowrap' }}>{row.fDate || '-'}</StyledTableCell>
        <StyledTableCell align="center" sx={{ whiteSpace: 'nowrap' }}>{row.cDate || '-'}</StyledTableCell>
        <StyledTableCell title={row.synopsis || '-'}>
          <Typography variant="body2" sx={cellTextClampStyle}>
            {row.synopsis || '-'}
          </Typography>
        </StyledTableCell>
        <StyledTableCell align="center">{row.complt || '-'}</StyledTableCell>
      </StyledTableRow>

      {/* Add Comment Dialog */}
      <Dialog
        open={openCommentModal}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleCloseCommentModal}
        maxWidth={maxWidth}
        fullWidth
        aria-labelledby="comment-dialog-title"
      >
        <DialogTitle id="comment-dialog-title">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t("text.AddEditCommentsFor")} {selectedRowInfo?.fileNo}</Typography>
            <IconButton aria-label="close" onClick={handleCloseCommentModal}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        {/* Make DialogContent position relative to allow absolute positioning inside */}
        <DialogContent dividers sx={{ position: 'relative', overflowY: 'auto' }}>

          {/* Wrapper Box for content that might be blurred */}
          <Box
            sx={{
              // Apply blur and disable interaction if active is true
              filter: active ? 'blur(4px)' : 'none',
              pointerEvents: active ? 'none' : 'auto',
              transition: 'filter 0.3s ease-in-out', // Smooth transition for blur
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              {t("text.FileName")}: {selectedRowInfo?.pdfname} (ID: {selectedRowInfo?.pfid})
            </Typography>

            {/* Wrap table in Paper with horizontal scroll */}
            <Paper elevation={0} sx={{ mt: 1, mb: 2, overflowX: 'auto', border: theme => `1px solid ${theme.palette.divider}` }}>
              <Table size="small" sx={{ minWidth: 900 }}> {/* Increased minWidth */}
                <TableHead sx={{ backgroundColor: theme => theme.palette.grey[100] }}> {/* Lighter header */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t("text.PageNoFrom")}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>{t("text.PageNoTo")}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>{t("text.PhysicalSerialNo")}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>{t("text.RelatedOficer")}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>{t("text.PhysicalIndex")}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>{t("text.Comments")}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t("text.Action")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map((commentRow, idx) => (
                    <TableRow key={idx} sx={{ '& > td': { verticalAlign: 'top' } }}> {/* Align top */}
                      <TableCell>
                        <TextField type="number" size="small" fullWidth value={commentRow.pgNF} onChange={(e) => handleInputChange(e, idx, "pgNF")} InputProps={{ inputProps: { min: 0 } }} onFocus={(e) => e.target.select()} />
                      </TableCell>
                      <TableCell>
                        <TextField type="number" size="small" fullWidth value={commentRow.pgNU} onChange={(e) => handleInputChange(e, idx, "pgNU")} InputProps={{ inputProps: { min: commentRow.pgNF || 0 } }} onFocus={(e) => e.target.select()} /> {/* Ensure To >= From */}
                      </TableCell>
                      <TableCell>
                        <TextField type="text" size="small" fullWidth value={commentRow.phySNo} onChange={(e) => handleInputChange(e, idx, "phySNo")} />
                      </TableCell>
                      <TableCell>
                        <TextField type="text" size="small" fullWidth value={commentRow.relOfficer} onChange={(e) => handleInputChange(e, idx, "relOfficer")} />
                      </TableCell>
                      <TableCell>
                        <TextField type="text" size="small" fullWidth value={commentRow.phyIndex} onChange={(e) => handleInputChange(e, idx, "phyIndex")} />
                      </TableCell>
                      <TableCell>
                        <TextField type="text" size="small" fullWidth multiline minRows={1} maxRows={3} value={commentRow.comments} onChange={(e) => handleInputChange(e, idx, "comments")} /> {/* Limit rows */}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={t("text.RemoveRow")} arrow>
                          <span> {/* Span needed for tooltip on disabled button */}
                            <IconButton size="small" color="error" onClick={() => removeRow(idx)} disabled={tableData.length <= 1}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
            <Button
              startIcon={<AddCircleOutline />}
              onClick={addMoreRow}
              size="small"
              variant="outlined"
              sx={{ mt: 0, textTransform: 'none' }} // Reduced margin top
            >
              {t("text.AddRow")}
            </Button>
          </Box> {/* End of potentially blurred content */}

          {/* Centered Button - Renders only when active is true */}
          {active && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%', // Center vertically
                left: '50%', // Center horizontally
                transform: 'translate(-50%, -50%)', // Adjust for element size
                zIndex: 10, // Ensure it's above the blurred content
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 'auto', // Adjust as needed
              }}
            >
              <Button
                startIcon={<AddCircleOutline />}
                onClick={() => navigate("/DocManagement/SplitPDFToJump")}
                size="medium" // Maybe make it slightly larger
                variant="contained" // Make it stand out more
                color="primary" // Or another prominent color
                sx={{
                  textTransform: 'none',
                  boxShadow: 6, // Add shadow for better visibility
                }}
              >
                Go to Next Step
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ padding: '16px 24px', borderTop: theme => `1px solid ${theme.palette.divider}` }}>
          {/* Disable actions if active (and content is blurred)? Optional */}
          <Button onClick={handleCloseCommentModal} variant="outlined" color="secondary" disabled={active}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitComments}
            variant="contained"
            color="primary"
            disabled={isSubmittingComment || active} // Disable if active
            startIcon={isSubmittingComment ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmittingComment ? t("text.Saving") : t("text.SaveComments")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View PDF Dialog */}
      <Dialog
        open={openPdfModal}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClosePdfModal}
        maxWidth="xl" // Use XL for PDFs
        fullWidth
        aria-labelledby="pdf-dialog-title"
        PaperProps={{ sx: { height: '90vh' } }} // Control dialog paper height
      >
        <DialogTitle sx={{ py: 1.5, borderBottom: theme => `1px solid ${theme.palette.divider}` }}> {/* Adjust padding & add border */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {/* Truncate title if too long */}
            <Typography variant="h6" component="div" noWrap sx={{ maxWidth: 'calc(100% - 50px)' }}>
              {selectedRowInfo?.pdfname || 'View PDF'}
            </Typography>
            {/* <IconButton aria-label="close" onClick={handleClosePdfModal}>
              <CloseIcon />
            </IconButton> */}
          </Stack>
        </DialogTitle>
        {/* <DialogContent sx={{ p: 0, overflow: 'hidden', flexGrow: 1 }}>
          {isLoadingPdf ? (
            <Box display="flex" flexDirection="column" // Stack vertically
              justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>{t("text.LoadingPDF")}...</Typography>
            </Box>
          ) : pdfData ? (
            <iframe
              src={pdfData}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={selectedRowInfo?.pdfname || 'PDF Document'}
            />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography color="textSecondary">{t("text.NoPDFPreviewAvailable")}</Typography>
            </Box>
          )}
        </DialogContent> */}
        <DialogContent sx={{ p: 0, overflow: 'hidden', flexGrow: 1 }}>
          {isLoadingPdf ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>{t("text.LoadingPDF")}...</Typography>
            </Box>
          ) : pdfData ? (
            <>
              <iframe
                src={pdfData}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: 'block'
                }}
                title={selectedRowInfo?.pdfname || 'PDF Document'}
                onLoad={() => console.log("PDF loaded successfully")}
                onError={() => toast.error(t("text.FailedLoadPDF"))}
              />
              <Box sx={{ position: 'absolute', right: 16, top: 16, zIndex: 1 }}>
                <IconButton
                  onClick={() => setOpenPdfModal(false)}
                  color="primary"
                  aria-label="Close PDF viewer"
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography color="textSecondary">{t("text.NoPDFPreviewAvailable")}</Typography>
              <Box sx={{ position: 'absolute', right: 16, top: 16, zIndex: 1 }}>
                <IconButton
                  onClick={() => setOpenPdfModal(false)}
                  color="primary"
                  aria-label="Close PDF viewer"
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}

// --- Main DocumentIndexing Component ---
export default function DocumentIndexing() {
  const { t } = useTranslation();
  const [isFilterExpanded, setIsFilterExpanded] = useState(true); // Start expanded maybe?
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [optionSubSubject, setOptionSubSubject] = useState([{ value: "-1", label: t("text.SelectFileNo") }]);
  const [optionDocument, setOptionDocument] = useState([{ value: "-1", label: t("text.SelectFileType") }]);
  const [optionFileClass, setOptionFileClass] = useState([{ value: "-1", label: t("text.SelectDepartment") }]);
  const [optionFileStatus, setOptionFileStatus] = useState([{ label: t("text.SelectFileStatus") }]);

  const printRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          getSubSubjectData(),
          getDocumentData(),
          getDepartmentData(),
          getFileStatusData()
        ]);
        // Optional: Trigger initial search on load if desired
        // formik.submitForm();
      } catch (error) {
        console.error("Error fetching initial dropdown data:", error);
        toast.error(t("text.FailedLoadFilters")); // Use translation
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // --- Data Fetching Functions (Keep existing logic, ensure robust error handling) ---
  const getSubSubjectData = async () => {
    const collectData = { docMid: -1, fileTypId: -1, 
      //@ts-ignore
      divisionid: devId === "null" ? -1 : devId,
      
      subsubjId: -1, user_Id: -1, fileNo: "" };
    try {
      const res = await axios.post(`${HOST_URL.HOST_URL2}DocMangr/GetDocMangr`, collectData);
      if (res.data?.isSuccess && Array.isArray(res.data.data)) {
        const arr = res.data.data
          .filter((item: any) => item && item.fileNo) // Ensure item and fileNo exist
          .map((item: any) => ({
            label:item.fileNo ? `${item.fileNo} (${item.synopsis})` : "_ _",
            value: String(item.docMid) ,
            fileNo: item.fileNo
          }));
        setOptionSubSubject([{ value: "-1", label: t("text.SelectFileNo") }, ...arr]);
      } else {
        console.warn("SubSubject data fetch warning:", res.data?.mesg);
        setOptionSubSubject([{ value: "-1", label: t("text.SelectFileNo") }]);
      }
    } catch (error) {
      console.error("Error fetching SubSubject:", error);
      setOptionSubSubject([{ value: "-1", label: t("text.SelectFileNo") }]); // Reset on error
      toast.error(t("text.ErrorFetchSubSubject"));
    }
  };

  const getDocumentData = async () => {
    const collectData = { doc_id: -1, user_id: "" };
    try {
      const res = await axios.post(`${HOST_URL.HOST_URL2}document/Getdocument`, collectData);
      if (res.data?.isSuccess && Array.isArray(res.data.data)) {
        const arr = res.data.data
          .filter((item: any) => item && item.doc_shortname)
          .map((item: any) => ({
            label: item.doc_shortname,
            value: String(item.doc_id) // Ensure value is string
          }));
        setOptionDocument([{ value: "-1", label: t("text.SelectFileType") }, ...arr]);
      } else {
        console.warn("Document data fetch warning:", res.data?.mesg);
        setOptionDocument([{ value: "-1", label: t("text.SelectFileType") }]);
      }
    } catch (error) {
      console.error("Error fetching Document Types:", error);
      setOptionDocument([{ value: "-1", label: t("text.SelectFileType") }]);
      toast.error("Error Fetch Doc Types");
    }
  };

  const getDepartmentData = async () => {
    const collectData = { dept_id: -1 };
    try {
      const res = await axios.post(`${HOST_URL.HOST_URL2}Department/GetDepartmentmaster`, collectData);
      if (res.data?.isSuccess && Array.isArray(res.data.data)) {
        const arr = res.data.data
          .filter((item: any) => item && item.dept_name)
          .map((item: any) => ({
            label: item.dept_name,
            value: String(item.dept_id) // Ensure value is string
          }));
        setOptionFileClass([{ value: "-1", label: t("text.SelectDepartment") }, ...arr]);
      } else {
        console.warn("Department data fetch warning:", res.data?.mesg);
        setOptionFileClass([{ value: "-1", label: t("text.SelectDepartment") }]);
      }
    } catch (error) {
      console.error("Error fetching Departments:", error);
      setOptionFileClass([{ value: "-1", label: t("text.SelectDepartment") }]);

    }
  };

  const getFileStatusData = async () => {
    try {
      const res = await axios.post(`${HOST_URL.HOST_URL2}FileStatus/GetFileStatus`);
      if (res.data?.isSuccess && Array.isArray(res.data.data)) {
        const arr = res.data.data
          .filter((item: any) => item && item.fStatus)
          .map((item: any) => ({
            label: item.fStatus
          }));
        setOptionFileStatus([{ label: t("text.SelectFileStatus") }, ...arr]);
      } else {
        console.warn("FileStatus data fetch warning:", res.data?.mesg);
        setOptionFileStatus([{ label: t("text.SelectFileStatus") }]);
      }
    } catch (error) {
      console.error("Error fetching File Status:", error);
      setOptionFileStatus([{ label: t("text.SelectFileStatus") }]);
      toast.error(t("text.ErrorFetchFileStatus"));
    }
  };

  // --- Formik Setup ---
  interface FormValues {
    fromDate: string | null;
    toDate: string | null;
    divisionid: string; // SubSubject/FileNo ID (use string for Autocomplete value)
    fileTypId: string; // Document Type ID (use string for Autocomplete value)
    filestatus: string; // Label from Autocomplete
    textSearch: string;
    // docMid is likely redundant if divisionid is used for the same purpose
    // user_Id might not be needed for filtering unless specific user's docs
  }

  const initialValues: FormValues = {
    fromDate: null,
    toDate: null,
    divisionid: "-1", // Default "Select..." value
    fileTypId: "-1", // Default "Select..." value
    filestatus: "", // No default status selection label
    textSearch: "",
  };

const devId = getSecureDevId();

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: async (values: any) => {
      setIsLoading(true);
      setData([]);

      // Prepare payload for API
      const payload = {
        fromDate: values.fromDate || "", // API might expect empty string not null
        toDate: values.toDate || "",
        //@ts-ignore
        divisionid:"",
        //divisionid: devId === "null" ? "" : String(devId), // Send empty string if default selected
        fileTypId: values.fileTypId === "-1" ? "" : values.fileTypId,   // Send empty string if default selected
        filestatus: values.filestatus === t("text.SelectFileStatus") ? "" : values.filestatus, // Send empty string if default label selected
        textSearch: values.textSearch.trim(),
        docMid: "", // Keep if API requires, otherwise remove
        subsubjId: -1, // Keep if API requires, otherwise remove
        user_Id: -1,   // Keep if API requires, otherwise remove
        fileNo: values?.fileNo || "",

      };
      // Remove empty string keys if API doesn't like them (optional)
      Object.keys(payload).forEach(key => {
        if (payload[key as keyof typeof payload] === "") {
          // delete payload[key as keyof typeof payload]; // Or set to null/undefined based on API needs
        }
      });

      // console.log("Submitting Filter:", payload);

      try {
        const response = await axios.post(
          `${HOST_URL.HOST_URL2}DocMangr/GetDocMangrForIndex`,
          payload
        );
        // console.log("API Response:", response.data);

        if (response.data.isSuccess === true) {
          if (Array.isArray(response.data.data) && response.data.data.length > 0) {
            setData(response.data.data);
            // toast.success(response.data.mesg || t("text.DataLoadedSuccess")); // Optional success message
          } else {
            setData([]);
            toast.info(t("text.NoDocumentsFound")); // More informative message
          }
          // Collapse filter only if data is found? Or always? User preference.
          // setIsFilterExpanded(false);
        } else {
          setData([]);
          toast.error(response.data.mesg || t("text.FailedFetchData"));
        }
      } catch (error) {
        console.error("Error fetching indexed data:", error);
        toast.error(t("text.ErrorFetchingData"));
        setData([]);
      } finally {
        setIsLoading(false);
      }
    },
  });

  // --- Print Function (Minor style improvements) ---
  const handlePrint = () => {
    const printContent: any | null = printRef.current;
    if (printContent) {
      const printWindow = window.open('', '', 'height=800,width=1100');
      if (printWindow) {
        printWindow.document.write('<html><head><title>Document Index Report</title>');
        // Add more comprehensive styles mimicking the screen
        printWindow.document.write(`
                <style>
                    body { font-family: sans-serif; padding: 15px; }
                    table { width: 100%; border-collapse: collapse; margin-top: 15px; }
                    th, td { border: 1px solid #ccc; padding: 8px 10px; text-align: left; font-size: 12px; vertical-align: top; }
                    th { background-color: #1976D2; color: white; font-weight: 600; white-space: nowrap; }
                    tr:nth-child(even) { background-color: #f2f2f2; } /* Optional alternating rows */
                    h1 { text-align: center; font-size: 18px; margin-bottom: 20px; }
                    /* Attempt basic text clamping for print (CSS support varies) */
                    .print-clamp {
                        overflow: hidden;
                        display: -webkit-box;
                        -webkit-line-clamp: 3;
                        -webkit-box-orient: vertical;
                        max-height: 4.5em; /* Adjust if needed */
                        line-height: 1.5em;
                    }
                    /* Hide action buttons in print */
                    .no-print { display: none; }
                    @media print {
                        body { padding: 5px; } /* Reduce padding for print */
                        th, td { font-size: 10px; padding: 5px; } /* Smaller font for print */
                        h1 { font-size: 16px; margin-bottom: 15px; }
                    }
                </style>
            `);
        printWindow.document.write('</head><body>');
        printWindow.document.write('<h1>Document Index Report</h1>');

        // Clone the table and modify for printing (remove action buttons)
        const tableClone = printContent.cloneNode(true) as HTMLElement;

        // Add 'no-print' class to action cells/buttons headers and body cells
        const headerCells = tableClone.querySelectorAll('thead th');
        if (headerCells[1]) headerCells[1].classList.add('no-print'); // Hide Actions header

        const bodyRows = tableClone.querySelectorAll('tbody tr');
        bodyRows.forEach(row => {
          const cells = row.querySelectorAll('td');
          if (cells[1]) cells[1].classList.add('no-print'); // Hide Actions cell content
          // Add print-clamp class to relevant cells
          if (cells[3]) cells[3].querySelector('p')?.classList.add('print-clamp'); // FileDef
          if (cells[4]) cells[4].querySelector('p')?.classList.add('print-clamp'); // SubSubject
          if (cells[5]) cells[5].querySelector('p')?.classList.add('print-clamp'); // Keywords
          if (cells[8]) cells[8].querySelector('p')?.classList.add('print-clamp'); // Synopsis
        });


        printWindow.document.write(tableClone.outerHTML); // Write modified table HTML
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        // Delay print slightly to allow styles to apply
        setTimeout(() => {
          printWindow.print();
          // printWindow.close(); // Optional: Close after print
        }, 250);
      } else {
        toast.error(t("text.CouldNotOpenPrint"));
      }
    } else {
      toast.error(t("text.CouldNotFindTable"));
    }
  };

  // --- Render Component ---
  return (
    // Added padding to the main container
    <Box sx={{ p: { xs: 1, sm: 2 }, backgroundColor: '#f8f9fa', minHeight: 'calc(100vh - 64px)' /* Adjust based on AppBar height */ }}>
      <ToastApp />
      <Paper
        elevation={2} // Slightly reduced shadow
        sx={{
          width: '100%',
          overflow: 'hidden', // Keep overflow hidden on Paper
          p: { xs: 1.5, sm: 2 }, // Consistent padding
        }}
      >
        {/* Header Section */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          mb={2}
        >
          <Typography variant="h5" component="div" fontWeight="600"> {/* Slightly bolder */}
            {t("text.DocumentIndexing")}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined" // Changed filter to outlined
              color="primary"
              startIcon={<FilterListIcon />}
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              aria-expanded={isFilterExpanded}
              aria-controls="filter-collapse-content"
              sx={{ textTransform: 'none' }}
            >
              {t("text.Filter")}
            </Button>
            <Button
              variant="outlined" // Changed print to outlined
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={data.length === 0} // Disable if no data
              sx={{ textTransform: 'none' }}
            >
              {t("text.Print")}
            </Button>
          </Stack>
        </Stack>

        {/* Collapsible Filter Section */}
        <Collapse in={isFilterExpanded} timeout="auto" unmountOnExit>
          {/* Added Card for better visual grouping of filters */}
          <Card variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'background.paper' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 'medium' }}>
              Filter Options
            </Typography>
            <Box component="form" onSubmit={formik.handleSubmit} id="filter-collapse-content">
              <Grid container spacing={2.5}> {/* Increased spacing slightly */}
                {/* Filter Inputs */}
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    disablePortal
                    id="filter-divisionid" // SubSubject/FileNo
                    options={optionSubSubject}

                    fullWidth
                    size="small"

                    onChange={(event, newValue:any) => {
                      formik.setFieldValue("fileNo", newValue?.fileNo); // Use ?? for nullish coalescing
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label={t("text.SelectFileNo")} />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    disablePortal
                    id="filter-fileTypId" // Document Type
                    options={optionDocument}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    fullWidth
                    size="small"
                    value={optionDocument.find(opt => opt.value === formik.values.fileTypId) || null}
                    onChange={(event, newValue) => {
                      formik.setFieldValue("fileTypId", newValue?.value ?? "-1");
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label={t("text.SelectFileType")} />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    disablePortal
                    id="filter-filestatus"
                    options={optionFileStatus}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(option, value) => option.label === value.label}
                    fullWidth
                    size="small"
                    // Find by label, handle case where initial value might be ""
                    value={optionFileStatus.find(opt => opt.label === formik.values.filestatus && formik.values.filestatus !== "") || null}
                    onChange={(event, newValue) => {
                      formik.setFieldValue("filestatus", newValue?.label || ""); // Set to empty string if cleared
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label={t("text.SelectFileStatus")} />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label={t("text.PeriodFrom")}
                    size="small"
                    type="date"
                    fullWidth
                    name="fromDate"
                    id="fromDate"
                    value={formik.values.fromDate || ''}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label={t("text.PeriodTo")}
                    size="small"
                    type="date"
                    fullWidth
                    name="toDate"
                    id="toDate"
                    value={formik.values.toDate || ''}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: formik.values.fromDate || undefined }} // Set min date based on fromDate
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label={t("text.Search") + " (" + "Keywords Etc" + ")"}
                    size="small"
                    type="text"
                    fullWidth
                    name="textSearch"
                    id="textSearch"
                    value={formik.values.textSearch}
                    onChange={formik.handleChange}
                    InputProps={{ /* Optional: Add SearchIcon */ }}
                  />
                </Grid>

                {/* Filter Actions */}
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Button
                      type="button"
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        formik.resetForm();
                        setData([]); // Clear results on clear
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      {t("text.Clear")}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isLoading}
                      sx={{ textTransform: 'none' }}
                      startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                      {isLoading ? "Searching" + "..." : t("text.Search")}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Collapse>

        {/* Use Divider only if filter is potentially collapsible */}
        {isFilterExpanded && <Divider sx={{ my: 1 }} />}

        {/* Table Section - Added relative positioning for loading overlay */}
        <Box sx={{ position: 'relative', mt: 1 }}>
          {/* Loading Overlay */}
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)', // Semi-transparent overlay
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10, // Ensure it's above the table
                borderRadius: theme => theme.shape.borderRadius, // Match Paper's border radius
              }}
            >
              <CircularProgress />
              <Typography sx={{ mt: 1.5 }} color="text.secondary">
                Loading Data
              </Typography>
            </Box>
          )}

          {/* Table Container */}
          <TableContainer component={Paper} variant="outlined" ref={printRef} sx={{ maxHeight: '65vh', border: 'none' /* Remove border if Paper provides enough */ }}>
            <Table stickyHeader aria-label="document index table" size="medium"> {/* Try medium size */}
              <TableHead>
                {/* Header Row */}
                <TableRow>
                  {/* Define Column Headers (Adjusted for combined actions) */}
                  <StyledTableCell align="center" sx={{ width: '5%' }}>{t("text.SrNo")}</StyledTableCell>
                  <StyledTableCell sx={{ width: '10%' }}>{t("text.Action")}</StyledTableCell>
                  <StyledTableCell sx={{ width: '15%' }}>{t("text.PdfFileName")}</StyledTableCell>
                  <StyledTableCell sx={{ width: '15%' }}>{t("text.FileDef")}</StyledTableCell>
                  <StyledTableCell sx={{ width: '15%' }}>{t("text.SubSubject")}</StyledTableCell>
                  <StyledTableCell sx={{ width: '15%' }}>{t("text.SearchKeywords")}</StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: '8%' }}>{t("text.DateFrom")}</StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: '8%' }}>{t("text.DateTo")}</StyledTableCell>
                  <StyledTableCell sx={{ width: '20%' }}>{t("text.Synopsis")}</StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: '5%' }}>{t("text.Complete")}</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Show message only when not loading and data is empty */}
                {!isLoading && data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ p: 4, border: 0 }}> {/* Adjusted colspan */}
                      <Typography color="text.secondary">
                        {t("text.NoDataAvailable")}
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        Try Adjusting Filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((rowData, index) => (
                    <Row key={rowData.pdFid || `row-${index}`} row={rowData} index={index} /> // Use a unique key like pdFid
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box> {/* End Relative Box */}
      </Paper>
    </Box>
  );
}